import React, { useState, useEffect, useRef } from 'react';
import {
  UserIcon,
  UserGroupIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  CheckIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { useUI } from '../../contexts/UIContext';
import { apiRequest } from '../../utils/apiUtils';
import LoadingSpinner from '../common/LoadingSpinner';

/**
 * AssignmentDropdown Component
 *
 * Provides a dropdown UI for assigning issues to users or teams.
 * Features:
 * - Search for users and teams
 * - Quick "Assign to me" action
 * - Unassign functionality
 * - Visual indication of current assignment
 */
const AssignmentDropdown = ({ issue, onAssignmentChange }) => {
  const { user } = useAuth();
  const { showNotification } = useUI();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Search for users and teams
  useEffect(() => {
    const searchEntities = async () => {
      if (!searchQuery || searchQuery.length < 1) {
        setSearchResults([]);
        return;
      }

      try {
        setLoading(true);

        // Search for users (from /api/profiles endpoint)
        const userResponse = await apiRequest(`/profiles?search=${encodeURIComponent(searchQuery)}&limit=10`);
        const users = userResponse.success ? (userResponse.users || []) : [];

        // Search for teams
        const teamResponse = await apiRequest(`/teams?search=${encodeURIComponent(searchQuery)}&limit=10`);
        const teams = teamResponse.success ? (teamResponse.teams || []) : [];

        // Combine results with type indicators
        const combinedResults = [
          ...users.map(u => ({ ...u, type: 'user' })),
          ...teams.map(t => ({ ...t, type: 'team' }))
        ];

        setSearchResults(combinedResults);
      } catch (error) {
        console.error('Error searching for assignment targets:', error);
      } finally {
        setLoading(false);
      }
    };

    const debounceTimer = setTimeout(() => {
      searchEntities();
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [searchQuery]);

  const handleAssign = async (target) => {
    try {
      setAssigning(true);

      const assignmentData = {};
      if (target.type === 'user') {
        assignmentData.assigned_to = target.id;
        // Clear team assignment when assigning to user
        assignmentData.assigned_team = null;
        assignmentData.assigned_to_team_id = null;
      } else if (target.type === 'team') {
        assignmentData.assigned_team = target.name;
        assignmentData.assigned_to_team_id = target.id;
        // Clear user assignment when assigning to team
        assignmentData.assigned_to = null;
      }

      const response = await apiRequest(`/issues/${issue.id}/assign`, {
        method: 'POST',
        data: assignmentData
      });

      if (response.success) {
        showNotification(
          `Assigned to ${target.type === 'user' ? (target.first_name && target.last_name ? `${target.first_name} ${target.last_name}` : target.email) : target.name}`,
          'success'
        );
        onAssignmentChange(response.issue);
        setIsOpen(false);
        setSearchQuery('');
      } else {
        showNotification('Failed to assign issue', 'error');
      }
    } catch (error) {
      console.error('Error assigning issue:', error);
      showNotification('Failed to assign issue', 'error');
    } finally {
      setAssigning(false);
    }
  };

  const handleAssignToMe = async () => {
    if (!user) return;

    try {
      setAssigning(true);

      const response = await apiRequest(`/issues/${issue.id}/assign`, {
        method: 'POST',
        data: {
          assigned_to: user.id,
          assigned_team: null,
          assigned_to_team_id: null
        }
      });

      if (response.success) {
        showNotification('Assigned to you', 'success');
        onAssignmentChange(response.issue);
        setIsOpen(false);
      } else {
        showNotification('Failed to assign issue', 'error');
      }
    } catch (error) {
      console.error('Error assigning issue to self:', error);
      showNotification('Failed to assign issue', 'error');
    } finally {
      setAssigning(false);
    }
  };

  const handleUnassign = async () => {
    try {
      setAssigning(true);

      const response = await apiRequest(`/issues/${issue.id}/assign`, {
        method: 'POST',
        data: {
          assigned_to: null,
          assigned_team: null,
          assigned_to_team_id: null
        }
      });

      if (response.success) {
        showNotification('Issue unassigned', 'success');
        onAssignmentChange(response.issue);
        setIsOpen(false);
      } else {
        showNotification('Failed to unassign issue', 'error');
      }
    } catch (error) {
      console.error('Error unassigning issue:', error);
      showNotification('Failed to unassign issue', 'error');
    } finally {
      setAssigning(false);
    }
  };

  // Get current assignment display
  const getCurrentAssignment = () => {
    if (issue.assigned_user) {
      const fullName = `${issue.assigned_user.first_name || ''} ${issue.assigned_user.last_name || ''}`.trim();
      return {
        type: 'user',
        display: fullName || issue.assigned_user.email,
        avatar: issue.assigned_user.avatar_url
      };
    } else if (issue.assigned_team_details) {
      return {
        type: 'team',
        display: issue.assigned_team_details.name,
        color: issue.assigned_team_details.color,
        icon: issue.assigned_team_details.icon
      };
    } else if (issue.assigned_team) {
      return {
        type: 'team',
        display: issue.assigned_team
      };
    }
    return null;
  };

  const currentAssignment = getCurrentAssignment();
  const isAssignedToCurrentUser = issue.assigned_to === user?.id;

  return (
    <div className="relative inline-block" ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={assigning}
        className={`
          inline-flex items-center px-3 py-2 border rounded-md text-sm font-medium transition-colors
          ${currentAssignment
            ? 'border-primary-300 bg-primary-50 text-primary-700 hover:bg-primary-100'
            : 'border-secondary-300 bg-white text-secondary-700 hover:bg-secondary-50'
          }
          disabled:opacity-50 disabled:cursor-not-allowed
        `}
      >
        {currentAssignment ? (
          <>
            {currentAssignment.type === 'user' ? (
              <UserIcon className="h-4 w-4 mr-2" />
            ) : (
              <UserGroupIcon className="h-4 w-4 mr-2" />
            )}
            <span>{currentAssignment.display}</span>
          </>
        ) : (
          <>
            <UserIcon className="h-4 w-4 mr-2" />
            <span>Assign</span>
          </>
        )}
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 z-50 mt-2 w-80 max-w-[calc(100vw-2rem)] bg-white rounded-lg shadow-lg ring-1 ring-black ring-opacity-5">
          {/* Header */}
          <div className="px-4 py-3 border-b border-secondary-200">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-secondary-900">Assign Issue</h3>
              <button
                onClick={() => setIsOpen(false)}
                className="text-secondary-400 hover:text-secondary-600"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>

            {/* Search Input */}
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-secondary-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search users or teams..."
                className="w-full pl-10 pr-3 py-2 border border-secondary-300 rounded-md text-sm focus:ring-primary-500 focus:border-primary-500"
                autoFocus
              />
            </div>
          </div>

          {/* Quick Actions */}
          <div className="px-2 py-2 border-b border-secondary-200">
            {!isAssignedToCurrentUser && (
              <button
                onClick={handleAssignToMe}
                disabled={assigning}
                className="w-full text-left px-3 py-2 rounded-md text-sm text-secondary-700 hover:bg-secondary-50 transition-colors flex items-center disabled:opacity-50"
              >
                <UserIcon className="h-4 w-4 mr-2 text-primary-600" />
                Assign to me
              </button>
            )}
            {currentAssignment && (
              <button
                onClick={handleUnassign}
                disabled={assigning}
                className="w-full text-left px-3 py-2 rounded-md text-sm text-danger-600 hover:bg-danger-50 transition-colors flex items-center disabled:opacity-50"
              >
                <XMarkIcon className="h-4 w-4 mr-2" />
                Unassign
              </button>
            )}
          </div>

          {/* Search Results */}
          <div className="max-h-64 overflow-y-auto">
            {loading ? (
              <div className="flex justify-center py-8">
                <LoadingSpinner size="sm" />
              </div>
            ) : searchQuery && searchResults.length === 0 ? (
              <div className="px-4 py-8 text-center">
                <p className="text-sm text-secondary-500">No users or teams found</p>
              </div>
            ) : searchQuery ? (
              <div className="py-2">
                {searchResults.map((result) => (
                  <button
                    key={`${result.type}-${result.id}`}
                    onClick={() => handleAssign(result)}
                    disabled={assigning}
                    className="w-full text-left px-4 py-2 hover:bg-secondary-50 transition-colors flex items-center justify-between disabled:opacity-50"
                  >
                    <div className="flex items-center min-w-0">
                      {result.type === 'user' ? (
                        <>
                          {result.avatar_url ? (
                            <img
                              src={result.avatar_url}
                              alt=""
                              className="h-6 w-6 rounded-full mr-3"
                            />
                          ) : (
                            <div className="h-6 w-6 rounded-full bg-secondary-200 flex items-center justify-center mr-3">
                              <UserIcon className="h-4 w-4 text-secondary-600" />
                            </div>
                          )}
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-secondary-900 truncate">
                              {result.first_name && result.last_name
                                ? `${result.first_name} ${result.last_name}`
                                : result.email}
                            </p>
                            {result.first_name && result.last_name && (
                              <p className="text-xs text-secondary-500 truncate">{result.email}</p>
                            )}
                          </div>
                        </>
                      ) : (
                        <>
                          <div
                            className="h-6 w-6 rounded mr-3 flex items-center justify-center"
                            style={{ backgroundColor: result.color || '#6B7280' }}
                          >
                            <UserGroupIcon className="h-4 w-4 text-white" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-secondary-900 truncate">{result.name}</p>
                            {result.description && (
                              <p className="text-xs text-secondary-500 truncate">{result.description}</p>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                    {((result.type === 'user' && result.id === issue.assigned_to) ||
                      (result.type === 'team' && result.id === issue.assigned_to_team_id)) && (
                      <CheckIcon className="h-4 w-4 text-primary-600 flex-shrink-0 ml-2" />
                    )}
                  </button>
                ))}
              </div>
            ) : (
              <div className="px-4 py-8 text-center">
                <MagnifyingGlassIcon className="h-8 w-8 text-secondary-300 mx-auto mb-2" />
                <p className="text-sm text-secondary-500">Search for users or teams to assign</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AssignmentDropdown;
